// The hikes list's ⋮ menu offers what the hike overview's More menu offers
// (Trello #1009). The overview is server-rendered, so the rules that decide its
// items live in Ruby; these are their twins for the list, which is Vue:
//
//   copyHikeMenuLabel  ↔ HikesHelper#copy_hike_menu_label   (app/helpers/hikes_helper.rb)
//   hikeEmbedBaseUrl   ↔ HikesHelper#hike_embed_url
//   hikeHasDays        ↔ `has_hike_days` in app/views/hikes/show.html.erb
//   hikeRouteCardsPath ↔ hike_route_cards_path → RouteCardsController#user_hike

/** Longest hike name the "Copy …" label shows before it truncates — HikesHelper::COPY_HIKE_LABEL_MAX_NAME_LENGTH. */
export const COPY_HIKE_LABEL_MAX_NAME_LENGTH = 30;

type Translate = (key: string, named?: Record<string, unknown>) => string;

interface MenuHike {
  name?: string | null;
  slug?: string | null;
  obfuscatedSlug?: string | null;
  madeOfficialAt?: string | null;
  mainCountry?: { slug?: string | null } | null;
  mainRegion?: { slug?: string | null } | null;
  hikeDays?: unknown[] | null;
}

/**
 * "Copy Wicklow Way" — the same label the overview and both apps use for
 * copying a hike into your own. A long name is cut to keep the menu narrow;
 * a blank one falls back to "Copy Hike".
 */
export function copyHikeMenuLabel(name: string | null | undefined, t: Translate): string {
  let trimmed = (name ?? '').trim();
  if (!trimmed) return t('shared.copy_hike');

  // Code points, not UTF-16 units, so an emoji is never split — as Ruby's String#[] counts.
  const chars = Array.from(trimmed);
  if (chars.length > COPY_HIKE_LABEL_MAX_NAME_LENGTH) {
    trimmed = `${chars.slice(0, COPY_HIKE_LABEL_MAX_NAME_LENGTH).join('').trimEnd()}…`;
  }
  return t('shared.copy_named_hike', { name: trimmed });
}

/** Route card and Itinerary need saved days; the overview hides both without them. */
export function hikeHasDays(hike: MenuHike | null | undefined): boolean {
  return Array.isArray(hike?.hikeDays) && hike!.hikeDays!.length > 0;
}

/** Opens the route card editor on the hike's first day (the server picks the day). */
export function hikeRouteCardsPath(hike: MenuHike): string {
  return `/hikes/users/${hike.obfuscatedSlug}/route-cards`;
}

/**
 * Base URL the Embed Map builder appends its options to. An official hike embeds
 * under its country/region/slug path; everything else under the obfuscated slug.
 */
export function hikeEmbedBaseUrl(hike: MenuHike, origin: string): string {
  const country = hike.mainCountry?.slug;
  const region = hike.mainRegion?.slug;
  if (hike.madeOfficialAt && country && region && hike.slug) {
    return `${origin}/hikes/${country}/${region}/${hike.slug}/embed`;
  }
  return `${origin}/hikes/users/${hike.obfuscatedSlug}/embed`;
}
